import React, { useState } from 'react';
import { XIcon } from './icons/InterfaceIcons';
import { generateCustomTest } from '../utils/testGenerator';
import Portal from './Portal';

interface TestConfigModalProps {
  paper: { shift: string; occurrences: number };
  onClose: () => void;
  onStartTest: (test: any) => void;
  exam: string;
  user: any;
}

export const TestConfigModal: React.FC<TestConfigModalProps> = ({ paper, onClose, onStartTest, exam, user }) => {
  const [testType, setTestType] = useState('full');
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [questionTypes, setQuestionTypes] = useState<string[]>(['multiple-choice']);
  const [duration, setDuration] = useState(180);
  const [questionCount, setQuestionCount] = useState(paper.occurrences);
  const [isGenerating, setIsGenerating] = useState(false);

  const subjects = exam === 'NDA' 
    ? ['Mathematics', 'General Ability Test', 'English', 'General Knowledge']
    : exam === 'NEET'
    ? ['Physics', 'Chemistry', 'Biology']
    : ['Physics', 'Chemistry', 'Mathematics'];

  const handleSubjectToggle = (subject: string) => {
    setSelectedSubjects(prev => 
      prev.includes(subject) 
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  };

  const handleQuestionTypeToggle = (type: string) => {
    setQuestionTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleStartTest = async () => {
    if (!user) {
      alert('Please sign in to take the test');
      return;
    }
    
    setIsGenerating(true);
    try {
      const config = {
        testType,
        selectedSubjects: testType === 'partial' ? selectedSubjects : subjects,
        questionTypes,
        duration,
        questionCount: testType === 'full' ? paper.occurrences : questionCount,
        paperName: paper.shift,
        exam
      };
      
      const test = await generateCustomTest(config);
      onStartTest(test);
      onClose(); // Close the modal after starting the test
    } catch (error) {
      console.error('Error generating test:', error);
      alert(`Failed to generate test: ${error.message}. Please try again.`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-brand-surface rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-brand-text-primary">Configure Test</h2>
              <button onClick={onClose} className="p-2 hover:bg-brand-bg rounded-lg">
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Paper: {paper.shift}</h3>
                <p className="text-brand-text-secondary">{paper.occurrences} total questions available</p>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Test Type</h4>
                <div className="space-y-2">
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      value="full"
                      checked={testType === 'full'}
                      onChange={(e) => setTestType(e.target.value)}
                      className="w-4 h-4 text-brand-primary"
                    />
                    <span>Full Test - All questions from this paper</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      value="partial"
                      checked={testType === 'partial'}
                      onChange={(e) => setTestType(e.target.value)}
                      className="w-4 h-4 text-brand-primary"
                    />
                    <span>Partial Test - Customize your test</span>
                  </label>
                </div>
              </div>

              {testType === 'partial' && (
                <>
                  <div>
                    <h4 className="font-semibold mb-3">Select Subjects</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {subjects.map(subject => (
                        <label key={subject} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selectedSubjects.includes(subject)}
                            onChange={() => handleSubjectToggle(subject)}
                            className="w-4 h-4 text-brand-primary"
                          />
                          <span className="text-sm">{subject}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Question Types</h4>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={questionTypes.includes('multiple-choice')}
                          onChange={() => handleQuestionTypeToggle('multiple-choice')}
                          className="w-4 h-4 text-brand-primary"
                        />
                        <span className="text-sm">Multiple Choice Questions</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={questionTypes.includes('integer')}
                          onChange={() => handleQuestionTypeToggle('integer')}
                          className="w-4 h-4 text-brand-primary"
                        />
                        <span className="text-sm">Integer Type Questions</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Number of Questions</h4>
                    <input
                      type="number"
                      min="1"
                      max={paper.occurrences}
                      value={questionCount}
                      onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-brand-border rounded-lg focus:ring-2 focus:ring-brand-primary bg-brand-surface-secondary text-brand-text-primary"
                    />
                  </div>
                </>
              )}

              <div>
                <h4 className="font-semibold mb-3">Duration (minutes)</h4>
                <input
                  type="number"
                  min="30"
                  max="300"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-brand-border rounded-lg focus:ring-2 focus:ring-brand-primary bg-brand-surface-secondary text-brand-text-primary dark:text-black"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={onClose}
                  className="px-6 py-2 text-brand-text-secondary hover:text-brand-text-primary transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartTest}
                  disabled={isGenerating || (testType === 'partial' && (selectedSubjects.length === 0 || questionTypes.length === 0))}
                  className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isGenerating ? 'Generating Test...' : 'Start Test'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};